import React, { useEffect, useRef } from 'react';
import './ConfirmModal.css';

interface ConfirmModalProps {
  isOpen: boolean;
  message: string;
  type?: 'warning' | 'error' | 'info';
  okText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  message,
  type = 'warning',
  okText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus the OK button when modal opens
      okButtonRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter') {
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onConfirm, onCancel]);

  if (!isOpen) return null;

  const getIcon = () => {
    if (type === 'warning') {
      return (
        <svg className="confirm-modal__icon confirm-modal__icon--warning" fill="#ffc107">
          <use xlinkHref="/img/icons.svg#icon-alert-triangle" />
        </svg>
      );
    } else if (type === 'error') {
      return (
        <svg className="confirm-modal__icon confirm-modal__icon--error" fill="url(#icon-gradient)">
          <use xlinkHref="/img/icons.svg#icon-x-circle" />
        </svg>
      );
    }
    return null;
  };

  const getBarColor = () => {
    switch (type) {
      case 'warning':
        return 'gold';
      case 'error':
        return '#dc3545';
      default:
        return '#ccc';
    }
  };

  return (
    <div className="confirm-modal" ref={modalRef}>
      <div className="confirm-modal__overlay" onClick={onCancel} />
      <div className="confirm-modal__box">
        <div className="confirm-modal__icon">
          {getIcon()}
        </div>
        {getIcon() && (
          <div 
            className="confirm-modal__bar"
            style={{ background: getBarColor() }}
          />
        )}
        <div className="confirm-modal__message">
          {message}
        </div>
        <div className="confirm-modal__actions">
          <button 
            className="btn btn--red confirm-modal__cancel"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button 
            ref={okButtonRef}
            className="btn btn--green confirm-modal__ok"
            onClick={onConfirm}
          >
            {okText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal; 